<template>
  <div v-if="categorias">
    <v-container class="pb-0">
      <promo-banner :mostrar-forma-pago="true"></promo-banner>
      <div class="mb-3">
        <v-chip
          class="mr-2"
          :outlined="!filtroMasVendidos"
          color="teal"
          @click="toggleMasVendidos"
        >
          <v-icon left small>mdi-star</v-icon>
          Más vendidos
        </v-chip>
      </div>
    </v-container>
    <div v-if="filtroMasVendidos" class="mt-2">
      <h2 class="font-weight-medium text-h5 mb-2 px-3">Más vendidos</h2>
      <v-container>
        <v-row v-if="loadingMasVendidos">
          <v-col v-for="i in 4" :key="i" cols="3">
            <v-skeleton-loader type="card"></v-skeleton-loader>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col v-for="(p,i) in masVendidos" :key="i" cols="3">
            <product-card :product="p"></product-card>
          </v-col>
        </v-row>
        <v-alert v-if="!loadingMasVendidos && !masVendidos.length" type="info" dense outlined>
          Todavía no hay productos destacados por ventas.
        </v-alert>
      </v-container>
    </div>
    <div v-else>
      <div
        v-for="(c,i) in categoriasVisibles"
        v-show="categoriaTieneContenido(c)"
        :key="`categoria_${i}`"
        class="mt-6 category-block"
      >
        <h2 class="font-weight-medium text-h5 mb-2">{{ c.descripcion }}</h2>
        <v-row>
          <productos-list-home
            :categoriaId="c.id"
            :categoria="c"
            @products-loaded="onProductsLoaded(c.id, $event)"
          ></productos-list-home>
        </v-row>
        <div
          v-for="(sub, si) in subcategoriasVisibles(c)"
          v-show="!categoriasVacias[sub.id]"
          :key="si"
          class="mt-6"
        >
          <h3 class="font-weight-medium text-h6 mb-2">{{ sub.descripcion }}</h3>
          <v-row>
            <productos-list-home
              :categoriaId="sub.id"
              :categoria="sub"
              @products-loaded="onProductsLoaded(sub.id, $event)"
            ></productos-list-home>
          </v-row>
        </div>
        <div v-if="c.totalProducts > 8" class="mt-4 text-center">
          <v-btn color="secondary" text>Ver más en {{ c.name }}</v-btn>
        </div>
      </div>
      <div v-if="mostrarBotonMasCategorias" class="mt-4 text-center">
        <v-btn color="secondary" text @click.prevent="cargarMasCategorias">Ver más categorías</v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ProductosListHome from '../components/ProductListHome.vue'
import ProductCard from '@/components/cards/ProductCard.vue'
import PromoBanner from '../components/PromoBanner.vue'
import axios from '@/axios/axios-api'
import { restoreCatalogScroll, peekCatalogPosition } from '@/helpers/catalogScroll.js'

export default {
  name: 'CategoriasHome',
  components: { ProductosListHome, ProductCard, PromoBanner },
  data () {
    return {
      limiteCategorias: 10,
      filtroMasVendidos: false,
      masVendidos: [],
      loadingMasVendidos: false,
      categoriasVacias: {}
    }
  },
  computed: {
    ...mapGetters('Categorias', ['categorias', 'categoriaDisponible']),
    ...mapGetters('App', ['tiendaCerrada']),
    categoriasFiltradas () {
      if (!this.categorias) return []
      if (this.tiendaCerrada) return this.categorias
      return this.categorias.filter(c => this.categoriaDisponible(c))
    },
    categoriasVisibles () {
      return this.categoriasFiltradas.slice(0, this.limiteCategorias)
    },
    mostrarBotonMasCategorias () {
      return this.categoriasFiltradas.length > this.limiteCategorias
    }
  },
  methods: {
    ...mapActions('Categorias', ['getCategorias']),
    cargarMasCategorias () {
      this.limiteCategorias = Math.min(
        this.limiteCategorias + 10,
        this.categoriasFiltradas.length
      )
    },
    subcategoriasVisibles (c) {
      if (!c || !c.subcategorias) return []
      if (this.tiendaCerrada) return c.subcategorias
      return c.subcategorias.filter(sub => this.categoriaDisponible(sub))
    },
    async toggleMasVendidos () {
      this.filtroMasVendidos = !this.filtroMasVendidos
      if (this.filtroMasVendidos) await this.loadMasVendidos()
    },
    async loadMasVendidos () {
      this.loadingMasVendidos = true
      try {
        const code = this.$clientCode()
        const { data } = await axios.get(`api/productos/mas-vendidos/${code}`, {
          params: { dias: 30, top: 20 }
        })
        this.masVendidos = (Array.isArray(data) ? data : [])
          .filter(p => Number(p.precio) > 0)
          .map(p => ({
            ...p,
            esMasVendido: true,
            cant: 1
          }))
      } catch (e) {
        this.masVendidos = []
      } finally {
        this.loadingMasVendidos = false
      }
    },
    onProductsLoaded (categoriaId, count) {
      this.$set(this.categoriasVacias, categoriaId, !(count > 0))
    },
    categoriaTieneContenido (c) {
      const propiaOk = !this.categoriasVacias[c.id]
      const subs = this.subcategoriasVisibles(c)
      const algunaSub = subs.some(s => !this.categoriasVacias[s.id])
      // Mientras no cargó, mostrar; si cargó y todo vacío, ocultar
      if (this.categoriasVacias[c.id] === undefined && !subs.length) return true
      if (propiaOk && this.categoriasVacias[c.id] === false) return true
      if (algunaSub) return true
      if (this.categoriasVacias[c.id] === true && subs.every(s => this.categoriasVacias[s.id] === true)) return false
      return true
    }
  },
  watch: {
    '$route.params.client': {
      handler: async function (newClient, oldClient) {
        if (newClient && newClient !== oldClient) {
          this.limiteCategorias = 10
          this.filtroMasVendidos = false
          this.masVendidos = []
          this.$store.commit('Categorias/setCategorias', [])
          await this.getCategorias()
        }
      },
      immediate: false
    }
  },
  async mounted () {
    await this.getCategorias()
    if (peekCatalogPosition()) {
      this.$nextTick(() => restoreCatalogScroll(this.$route))
    }
  }
}
</script>
